import { usePersonStore } from '~/stores/person';
import type { BaseAPIResponse } from '~/types/api';
import type { ConversationMessage } from '~/types/companies/conversations';
import { REMOVAL_ACTIVITY_FIGURES, type Exposure, type ExposureDetails, type ExposureDetailsResponse, type PersonExposure } from '~/types/person/details';
import { DIGITAL_FOOTPRINT_FIGURES, type DigitalFootprintFigures, type ExposureStats, type ExposureStatsWindow } from '~/types/person/exposure-stats';
import type { PotentialExposuresResponseData, SuppressionRecord } from '~/types/person/vault';
import { pbRequest, pbRequestRaw, pbRequestVoid } from '~/services/request';
import { ApiError } from '~/utils/errors';

const isFigure = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

/**
 * The figures `keys` names, when every one is present and a finite number. A missing, non-numeric or non-finite figure
 * is no figures at all: stating a number from it would print a figure the server never counted. A field beyond the keys
 * is ignored, so the server can add one without every client reading its figures as missing.
 */
function readFigures<K extends string>(value: unknown, keys: readonly K[]): Record<K, number> | null {
    if (typeof value !== 'object' || value === null) return null;

    const figures: Partial<Record<K, number>> = {};
    for (const key of keys) {
        const figure: unknown = Reflect.get(value, key);
        if (!isFigure(figure)) return null;
        figures[key] = figure;
    }

    // Every key was read as a figure just above.
    return figures as Record<K, number>;
}

// Pure API service for exposure-related operations - no state management
export const exposuresService = {
    async getExposure(personId: number, exposureId: string): Promise<Exposure> {
        const nuxtApp = useNuxtApp();

        return await pbRequest<Exposure>(nuxtApp.$api.routes.people.exposures.exposure(personId, exposureId), {
            failure: 'Failed to fetch exposure details',
        });
    },

    async getExposures(personId: number, limit: number = 50): Promise<PersonExposure[]> {
        const nuxtApp = useNuxtApp();

        return await pbRequest<PersonExposure[]>(nuxtApp.$api.routes.people.exposures.list(personId, limit), {
            failure: 'Failed to fetch exposures',
        });
    },

    async getPotentialExposures(personId: number): Promise<PotentialExposuresResponseData> {
        const nuxtApp = useNuxtApp();

        return await pbRequest<PotentialExposuresResponseData>(
            nuxtApp.$api.routes.people.exposures.data_exposed(personId),
            { method: 'GET', failure: 'Failed to fetch potential exposures' }
        );
    },

    async updateExposure(type: string, value: any, isMyExposure: boolean): Promise<SuppressionRecord> {
        const nuxtApp = useNuxtApp();
        const personStore = usePersonStore();

        if (!personStore.person?.id) {
            throw new ApiError('No person found', 404);
        }

        // The backend returns the saved record in the 201 body; an empty body means the
        // suppression did not persist and must surface as an error.
        return await pbRequest<SuppressionRecord>(
            nuxtApp.$api.routes.people.exposures.suppressions.update_suppressions(personStore.person.id),
            {
                method: 'PUT',
                body: {
                    type: type,
                    data: value,
                    me: isMyExposure,
                },
                expect: 201,
                failure: 'Failed to update exposure',
            }
        );
    },

    async getSuppressedRecords(): Promise<SuppressionRecord[]> {
        const nuxtApp = useNuxtApp();
        const personStore = usePersonStore();

        if (!personStore.person?.id) {
            throw new ApiError('No person found', 404);
        }

        const records = await pbRequest<SuppressionRecord[]>(
            nuxtApp.$api.routes.people.exposures.suppressions.get_suppressions(personStore.person.id),
            { method: 'GET', failure: 'Could not fetch suppressed records' }
        );

        const parsedRecords: SuppressionRecord[] = [];

        for (const record of records) {
            // Rows labeled `relatives` render from their own vault section. The compare is
            // widened because the record union carries only the singular form.
            if ((record.type as string) === 'relatives') {
                continue;
            }

            // The backend decodes the JSON, but a record can still arrive as a string.
            let processedData = record.data;
            if (typeof record.data === 'string' && record.data.match(/^{.*}$/)) {
                try {
                    processedData = JSON.parse(record.data);
                } catch (e) {
                    // If parsing fails, keep as string
                    processedData = record.data;
                }
            }

            parsedRecords.push({
                ...record,
                data: processedData,
            });
        }

        return parsedRecords;
    },

    async deleteSuppressedRecord(suppressionId: number): Promise<void> {
        const nuxtApp = useNuxtApp();
        const personStore = usePersonStore();

        if (!personStore.person?.id) {
            throw new ApiError('No person found', 404);
        }

        await pbRequestVoid(
            nuxtApp.$api.routes.people.exposures.suppressions.remove_suppression(personStore.person.id, suppressionId),
            { method: 'DELETE', failure: 'Failed to delete suppressed record' }
        );
    },

    async getExposureDetails(personId: number): Promise<ExposureDetails> {
        const nuxtApp = useNuxtApp();
        const failure = 'Failed to fetch exposure details';

        // The envelope is read whole: the removal figures sit in `meta`, beside the rows they were tallied with.
        const envelope = await pbRequestRaw<ExposureDetailsResponse>(nuxtApp.$api.routes.people.exposures.detail(personId), {
            failure,
        });

        // Without its rows the answer is not this endpoint's, so the whole read fails.
        if (!Array.isArray(envelope.data)) {
            throw new ApiError(failure, 404);
        }

        // Figures that are missing or malformed fail on their own: the rows beside them are still the member's to read.
        const removalActivity = readFigures(envelope.meta?.removal_activity, REMOVAL_ACTIVITY_FIGURES);
        if (!removalActivity) {
            console.error(
                `GET people/{person}/exposures/detail answered without meta.removal_activity as four finite figures (${REMOVAL_ACTIVITY_FIGURES.join(', ')}); its rows stand and its removal figures are unavailable:`,
                envelope.meta,
            );
        }

        return { companies: envelope.data, removalActivity };
    },

    async requestDeletion(companyId: number): Promise<void> {
        const nuxtApp = useNuxtApp();
        const personStore = usePersonStore();

        await pbRequestVoid(
            nuxtApp.$api.routes.people.companies.untrust_company(personStore.person?.id || 0, companyId),
            {
                method: 'POST',
                body: { delete: true },
                expect: 201,
                failure: 'Failed to request deletion',
            }
        );
    },

    async getExposureHistory(personId: number, exposureId: string): Promise<ConversationMessage[]> {
        const nuxtApp = useNuxtApp();

        return await pbRequest<ConversationMessage[]>(
            nuxtApp.$api.routes.people.exposures.exposure_history(personId, exposureId),
            { failure: 'Failed to fetch exposure history' }
        );
    },

    async getCompanyHistory(personId: number, companyId: number): Promise<ConversationMessage[]> {
        const nuxtApp = useNuxtApp();

        // A company nobody has written to yet has no conversation, which is an answer.
        const envelope = await pbRequestRaw<BaseAPIResponse<ConversationMessage[]>>(
            nuxtApp.$api.routes.people.exposures.company_history(personId, companyId),
            { failure: 'Failed to fetch company history' }
        );

        return envelope.data ?? [];
    },

    /**
     * The Digital Footprint figures for the member's last `months` months: the `digital_footprint` part of the one
     * exposure stats read, which is all the dashboard card states. An answer without them as four finite numbers fails
     * the read, so a figure the API did not send is never stated as a zero.
     */
    async getDigitalFootprint(personId: number, months: ExposureStatsWindow): Promise<DigitalFootprintFigures> {
        const nuxtApp = useNuxtApp();

        const stats = await pbRequest<ExposureStats>(nuxtApp.$api.routes.people.exposures.stats(personId, months), {
            failure: 'Failed to fetch exposure stats',
        });

        const footprint = readFigures(stats.digital_footprint, DIGITAL_FOOTPRINT_FIGURES);
        if (!footprint) {
            throw new ApiError(
                `GET people/{person}/exposures/stats answered without data.digital_footprint as four finite figures (${DIGITAL_FOOTPRINT_FIGURES.join(', ')})`,
                404,
            );
        }

        return footprint;
    },

    /**
     * Get exposures for a specific company
     * Returns detailed exposure data including screenshots and detected data.
     * The response mixes detail and remediation fields; callers pick their view
     * via the type parameter (assertion-based, like every usePbApi call).
     */
    async getExposuresForCompany<T = CompanyExposureDetail>(personId: number, companyId: number): Promise<T[]> {
        const nuxtApp = useNuxtApp();

        // A company with nothing on file answers with an empty list, which is an answer.
        const envelope = await pbRequestRaw<BaseAPIResponse<T[]>>(
            nuxtApp.$api.routes.people.companies.exposures(personId, companyId),
            { failure: 'Failed to fetch company exposures' }
        );

        return envelope.data ?? [];
    },

    /**
     * Get recent people search/affiliate exposures for a person
     * Returns exposures sorted by most recent activity (created_at, in_progress_at, closed_at)
     */
    async getRecentExposures(personId: number, limit: number = 6): Promise<RecentExposure[]> {
        const nuxtApp = useNuxtApp();

        // A member with no recent activity answers with an empty list, which is an answer.
        const envelope = await pbRequestRaw<BaseAPIResponse<RecentExposure[]>>(
            nuxtApp.$api.routes.people.exposures.recent(personId, limit),
            { failure: 'Failed to fetch recent exposures' }
        );

        return envelope.data ?? [];
    },

    /**
     * Submit a custom removal request for a URL
     * @param personId - The person ID
     * @param url - The URL where the person's data was found
     * @returns Custom removal response with exposure info
     */
    async submitCustomRemoval(personId: number, url: string): Promise<CustomRemovalResponse> {
        const nuxtApp = useNuxtApp();

        // The one call that reads a failure body: a 409 and a 403 each carry the exposure and
        // company the member is being told about, which no thrown ApiError can hand back.
        const { data, status, message } = await usePbApi<CustomRemovalResponse>(
            nuxtApp.$api.routes.people.exposures.custom_removal(personId),
            {
                method: 'POST',
                body: { url },
            }
        );

        if (status !== 200) {
            // Check if it's a duplicate exposure (already exists)
            if (status === 409 && data?.already_exists) {
                throw new CustomRemovalError(
                    message || 'A removal request for this URL is already in progress.',
                    status,
                    false, // not a license upgrade issue
                    data.exposure_id,
                    data.company,
                    true // already exists
                );
            }
            // Check if it's a license upgrade required error
            if (status === 403 && data?.requires_upgrade) {
                throw new CustomRemovalError(
                    message || 'License upgrade required',
                    status,
                    true,
                    data.exposure_id,
                    data.company
                );
            }
            throw new ApiError(message || 'Failed to submit custom removal request', status || 500);
        }

        return data as CustomRemovalResponse;
    },
};

// Type for company exposure detail response (used in data brokers expansion)
export interface CompanyExposureDetail {
    exposure_id: string;
    company_id: number;
    person_id: number;
    status: string;
    profile_url: string | null;
    created_at: string;
    in_progress_at: string | null;
    closed_at: string | null;
    last_action_at: string;
    detected_data: string[];
    timeline: {
        exposure_found: string | null;
        request_sent: string | null;
        verified_clean: string | null;
    };
    data: {
        name?: string | null;
        age?: string | null;
        date_of_birth?: string | null;
        addresses?: any[];
        phones?: string[];
        emails?: string[];
        relatives?: any[];
        location?: string | null;
        education?: string | null;
        occupation?: string | null;
        url?: string | null;
        screenshot_url?: string | null;
    } | null;
}

// Type for custom removal response
export interface CustomRemovalResponse {
    success: boolean;
    message: string;
    exposure_id?: string;
    requires_upgrade?: boolean;
    already_exists?: boolean;
    company?: {
        id: number;
        name: string;
        type: string | null;
    };
}

// Custom error class for custom removal that includes additional data
export class CustomRemovalError extends Error {
    status: number;
    requiresUpgrade: boolean;
    alreadyExists: boolean;
    exposureId?: string;
    company?: { id: number; name: string; type: string | null };

    constructor(
        message: string,
        status: number,
        requiresUpgrade: boolean = false,
        exposureId?: string,
        company?: { id: number; name: string; type: string | null },
        alreadyExists: boolean = false
    ) {
        super(message);
        this.name = 'CustomRemovalError';
        this.status = status;
        this.requiresUpgrade = requiresUpgrade;
        this.alreadyExists = alreadyExists;
        this.exposureId = exposureId;
        this.company = company;
    }
}

// Type for recent exposure response
export interface RecentExposure {
    exposure_id: string;
    company_id: number;
    status: string;
    activity_type: 'discovered' | 'in_progress' | 'closed';
    activity_date: string;
    created_at: string;
    in_progress_at: string | null;
    closed_at: string | null;
    profile_url: string | null;
    data: {
        first_name?: string | null;
        middle_name?: string | null;
        last_name?: string | null;
        name?: string | null;
        emails?: string[];
        phones?: string[];
        addresses?: { line1?: string; city?: string; state?: string; postal_code?: string }[];
        aka?: string[];
        relatives?: { first_name?: string; last_name?: string; name?: string; age?: string }[];
        age?: string | null;
        date_of_birth?: string | null;
        screenshot_full_path?: string | null;
        url?: string | null;
        search_url?: string | null;
        record_id?: string | null;
    } | null;
    company: {
        id: number;
        name: string;
        slug: string;
        type: string;
        logo: string | null;
    } | null;
}
